import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TrialWall from "../components/TrialWall";
import { useAuth } from "./AuthContext";
import { useAPI } from "./APIContext";
import { showToast } from "../components/Toast";
import { saveCreationDraft } from "../utils/creationDraft";

// Mounted once in App.js. Owns the TrialWall and the single decision every
// gated action makes:
//
//   const { requireEntitlement } = useEntitlementGate();
//   requireEntitlement({ trigger: "creation" }, () => doTheThing());
//
//  - entitled (trial, paid, grandfathered, comp) -> onAllowed() runs and the
//    page then does its own star-cost handling exactly as before
//  - signed-in without entitlement -> TrialWall opens
//  - guest -> TrialWall opens in its signup variant
//
// Also listens for the APIContext backstop event (403 SUBSCRIPTION_REQUIRED)
// so any creation path the client gates miss still lands on the wall.
//
// IMPORTANT: while the backend cutover flag is off it never sends
// subscription_required=true, and AuthContext's fallback marks premium tiers
// only — so requireEntitlement must NOT wall free users until the backend
// says the cutover is live (subscriptionStatus.subscriptionRequired).

const EntitlementGateContext = createContext(null);

export const EntitlementGateProvider = ({ children }) => {
  const {
    isAuthenticated,
    hasEntitlement,
    subscriptionStatus,
    waitForSubscriptionActivation,
    refreshSubscriptionStatus,
    updateUser,
    user,
  } = useAuth();
  const { apiClient } = useAPI();
  const [wallOpen, setWallOpen] = useState(false);
  const [wallContext, setWallContext] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const handledUpgradeRef = useRef(false);

  const openWall = useCallback((context = {}) => {
    setWallContext(context);
    setWallOpen(true);
  }, []);

  const closeWall = useCallback(() => setWallOpen(false), []);

  // Self-healing stale-cache backstop: whenever the wall opens for a
  // signed-in user, force-refresh their status past both caches (30-min
  // client, 5-min server). If they're actually entitled — activation poll
  // timed out, or they subscribed on another device — hasEntitlement flips
  // and the wall closes itself. Throttled to once a minute.
  const lastWallRefreshRef = useRef(0);
  useEffect(() => {
    if (!wallOpen || !isAuthenticated) return;
    const now = Date.now();
    if (now - lastWallRefreshRef.current < 60000) return;
    lastWallRefreshRef.current = now;
    refreshSubscriptionStatus?.();
  }, [wallOpen, isAuthenticated, refreshSubscriptionStatus]);

  useEffect(() => {
    if (wallOpen && isAuthenticated && hasEntitlement) {
      setWallOpen(false);
    }
  }, [wallOpen, isAuthenticated, hasEntitlement]);

  const cutoverLive = subscriptionStatus?.subscriptionRequired === true;

  const requireEntitlement = useCallback(
    (context, onAllowed) => {
      // Pre-cutover: the wall never blocks anyone — old behavior everywhere.
      if (!cutoverLive) {
        onAllowed?.();
        return true;
      }
      if (!isAuthenticated) {
        openWall({ ...context, guest: true });
        return false;
      }
      if (!hasEntitlement) {
        openWall(context);
        return false;
      }
      onAllowed?.();
      return true;
    },
    [cutoverLive, isAuthenticated, hasEntitlement, openWall],
  );

  // Checkout return: success_url now points back at wherever the user was
  // when they hit the wall (creation pages restore their saved draft on
  // mount). Settings keeps its own richer ?upgraded handler; everywhere else
  // this one welcomes them, polls until the entitlement lands, and cleans
  // the URL.
  useEffect(() => {
    if (location.pathname === "/settings") return;
    const params = new URLSearchParams(location.search);
    const upgraded = params.get("upgraded") === "true";
    const cancelled = params.get("cancelled") === "true";
    if (!upgraded && !cancelled) return;
    // Wait for auth to restore before consuming the params: the activation
    // poll no-ops without user.id, so firing early would burn the one-shot
    // handler, never poll, and strand the client on its cached NONE tier
    // (locked out despite a live subscription) until the cache TTL expired.
    if (!user?.id) return;
    if (handledUpgradeRef.current) return;
    handledUpgradeRef.current = true;

    if (upgraded) {
      // Google Ads conversion (mirrors Settings' handler for non-settings
      // return paths; server-side PostHog fires from the Stripe webhook)
      if (window.gtag) {
        const tier = params.get("tier") || "";
        const billing = params.get("billing") || "monthly";
        const tierPrices = { starter: 7.99, plus: 14.99, pro: 14.99, family: 24.99, unlimited: 49.99 };
        const monthlyPrice = tierPrices[tier] || 14.99;
        const value = billing === "annual" ? Math.round(monthlyPrice * 12 * 0.75 * 100) / 100 : monthlyPrice;
        window.gtag("event", "conversion", {
          send_to: "AW-17614735089/yVTZCOHgyJ0cEPGNrs9B",
          value,
          currency: "USD",
          transaction_id: `sub_${Date.now()}`,
        });
      }
      showToast("Welcome to Pixley! Picking up where you left off...", "success");
      setWallOpen(false);
      // Poll past the caches until RevenueCat reflects the new entitlement
      waitForSubscriptionActivation?.({
        untilChangedFrom: "NONE",
        timeoutMs: 90000,
        intervalMs: 2500,
      });
    }

    // Strip the checkout params so refreshes don't re-trigger any of this
    for (const k of ["upgraded", "cancelled", "tier", "billing"]) params.delete(k);
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ""}`, { replace: true });
  }, [location, navigate, waitForSubscriptionActivation, user?.id]);

  // Onboarding first-story auto-queue (iOS parity). completeOnboarding
  // stashes the adventure in pixley_first_story_pending; the moment the user
  // is entitled AND past the consent gate, the story is created. Lives here
  // (not in the consent gate) because the gate is SKIPPED for accounts that
  // already carry a consent record — iOS-consented users, retested accounts —
  // and the story must queue for them too.
  const firstStoryRunRef = useRef(false);
  useEffect(() => {
    if (!cutoverLive || !isAuthenticated || !hasEntitlement || !user?.id) return;
    if (user?.personalization?.parental_consent?.agreed !== true) return; // VPC first
    if (firstStoryRunRef.current) return;
    let pending = null;
    try {
      pending = JSON.parse(localStorage.getItem("pixley_first_story_pending") || "null");
    } catch {
      pending = null;
    }
    const idea = (pending?.idea || "").trim();
    if (!idea) return;
    if (Date.now() - (pending.savedAt || 0) > 24 * 60 * 60 * 1000) {
      localStorage.removeItem("pixley_first_story_pending");
      return;
    }
    firstStoryRunRef.current = true;
    localStorage.removeItem("pixley_first_story_pending"); // consume before async work
    const childName = (user?.username || "").trim();

    (async () => {
      try {
        const charsResp = await apiClient.getUserCharacters(user.id);
        const chars = (charsResp?.status && charsResp.data) || [];
        if (!chars.length) throw new Error("no characters");
        const chosen =
          chars.find(
            (c) => childName && (c.name || "").trim().toLowerCase() === childName.toLowerCase(),
          ) || chars[0];

        let title = `${chosen.name}'s First Adventure`;
        try {
          const t = await apiClient.generateTitle(chosen.name);
          if (t?.status && t.title) title = t.title;
        } catch (_) {}

        const resp = await apiClient.createStory(
          {
            user_id: user.id,
            title,
            description: idea,
            characters: [chosen.id],
            is_public: true,
            status: "requested",
          },
          60,
          "16:9",
          "ENGLISH",
        );
        if (!resp || resp.success === false || resp.error) {
          throw new Error(resp?.error || "create failed");
        }
        const balance = resp?.remaining_stars ?? resp?.data?.remaining_stars;
        if (balance !== undefined) {
          updateUser({ ...user, pixley_stars: balance });
        }
        showToast(
          `${childName || chosen.name}'s first story is being made! We'll email you when it's ready.`,
          "success",
          8000,
        );
      } catch (e) {
        // Restore the idea as a /create draft and take them there to finish
        try {
          saveCreationDraft("create-story", { storyDescription: idea });
        } catch (_) {}
        showToast("Let's finish making that first story!", "info");
        navigate("/create");
      }
    })();
  }, [cutoverLive, isAuthenticated, hasEntitlement, user, apiClient, navigate, updateUser]);

  // Backstop: server said SUBSCRIPTION_REQUIRED (client gate missed or state
  // was stale). AuthContext refreshes off the same event; we show the wall.
  useEffect(() => {
    const onSubRequired = (e) => {
      openWall({ trigger: "api_403", detail: e?.detail });
    };
    window.addEventListener("pixley:subscription-required", onSubRequired);
    return () =>
      window.removeEventListener("pixley:subscription-required", onSubRequired);
  }, [openWall]);

  return (
    <EntitlementGateContext.Provider
      value={{ requireEntitlement, openWall, closeWall, cutoverLive }}
    >
      {children}
      <TrialWall isOpen={wallOpen} onClose={closeWall} context={wallContext} />
    </EntitlementGateContext.Provider>
  );
};

export const useEntitlementGate = () => {
  const ctx = useContext(EntitlementGateContext);
  if (!ctx) {
    // Render-safe fallback for surfaces mounted outside the provider (none
    // in practice — App.js wraps all routes): allow rather than crash.
    return {
      requireEntitlement: (_c, onAllowed) => {
        onAllowed?.();
        return true;
      },
      openWall: () => {},
      closeWall: () => {},
      cutoverLive: false,
    };
  }
  return ctx;
};
